import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { ModalCloseButton } from "../../../Components/Molecules/ValutDialog";
import { PasswordConfirm } from "../../../Components/Molecules";
import { useStablesQuoteTimer } from "./hooks/useStablesQuoteTimer";
import { resolveBuyQuoteDisplay } from "./constants";
import {
  formatQuoteBrl,
  formatQuoteCrypto,
  formatQuotePercent,
  formatQuotePrice,
  formatQuoteTimer,
  QUOTE_DURATION_SEC,
  QUOTE_TITLES,
} from "./stablesQuoteDisplay";

const ACCEPT_ARM_TIMEOUT_MS = 2000;

const QuoteRow = ({ label, value, suffix, muted }) => (
  <div className="flex items-center justify-between gap-4 border-b border-gray-200/80 py-3 last:border-b-0">
    <span className={`text-sm font-bold ${muted ? "text-gray-500" : "text-brand-dark"}`}>{label}</span>
    <span className={`text-right text-sm font-bold ${muted ? "text-gray-500" : "text-brand-dark"}`}>
      {value}{" "}
      {suffix && <span className="font-semibold text-gray-400">{suffix}</span>}
    </span>
  </div>
);

export const StablesQuoteModal = ({
  open,
  quote,
  onClose,
  onExpire,
  onStepChange,
  refreshing = false,
  submitPassword,
  passwordError = null,
  passwordLoading = false,
  onDismissPasswordError,
  stepUpOptions = null,
  timerProps = null,
}) => {
  const [step, setStep] = useState("quote");
  const quoteDuration = quote?.expiresInSeconds ?? QUOTE_DURATION_SEC;
  const [acceptArmed, setAcceptArmed] = useState(false);
  const acceptArmTimeoutRef = useRef(null);
  const internalTimer = useStablesQuoteTimer({
    active: open && step === "quote" && !timerProps,
    quoteKey: `${quote?.id ?? ""}:${quote?.refreshedAt ?? ""}`,
    quoteDuration,
    refreshing,
    onExpire,
  });
  const { secondsLeft, barTransition, barWidth } = timerProps ?? internalTimer;

  const disarmAccept = useCallback(() => {
    if (acceptArmTimeoutRef.current) {
      clearTimeout(acceptArmTimeoutRef.current);
      acceptArmTimeoutRef.current = null;
    }
    setAcceptArmed(false);
  }, []);

  useEffect(() => {
    disarmAccept();
  }, [quote?.id, quote?.refreshedAt, disarmAccept]);

  useEffect(() => {
    if (!open) {
      setStep("quote");
      disarmAccept();
      onStepChange?.(false);
    }
  }, [open, disarmAccept, onStepChange]);

  useEffect(() => {
    onStepChange?.(step === "security");
  }, [onStepChange, step]);

  useEffect(() => {
    if (step === "security") return;
    setStep("quote");
  }, [quote?.id, quote?.refreshedAt, step]);

  useEffect(() => {
    if (refreshing) {
      disarmAccept();
    }
  }, [refreshing, disarmAccept]);

  useEffect(
    () => () => {
      if (acceptArmTimeoutRef.current) {
        clearTimeout(acceptArmTimeoutRef.current);
      }
    },
    []
  );

  if (!quote) return null;
  const isBuy = quote.operation === "buy";
  const isSwap = quote.operation === "swap";
  const isWithdraw = quote.operation === "withdraw";
  const buyDisplay = isBuy ? resolveBuyQuoteDisplay(quote) : null;

  const handleAcceptClick = () => {
    if (passwordLoading || refreshing || (!isWithdraw && secondsLeft === 0)) return;

    if (!acceptArmed) {
      setAcceptArmed(true);
      if (acceptArmTimeoutRef.current) {
        clearTimeout(acceptArmTimeoutRef.current);
      }
      acceptArmTimeoutRef.current = setTimeout(() => {
        acceptArmTimeoutRef.current = null;
        setAcceptArmed(false);
      }, ACCEPT_ARM_TIMEOUT_MS);
      return;
    }

    disarmAccept();
    // Compra/venda/swap: só confirmação por duplo clique — sem senha transacional.
    if (isBuy || isSwap || quote.operation === "sell") {
      void submitPassword?.();
      return;
    }
    setStep("security");
  };

  const handleSecurityBack = () => {
    if (passwordLoading) return;
    onDismissPasswordError?.();
    setStep("quote");
  };

  const acceptDisabled = passwordLoading || refreshing || (!isWithdraw && secondsLeft === 0);
  const acceptLabel = isWithdraw
    ? acceptArmed
      ? intl.get("stables.confirmWithdrawConfirm")
      : intl.get("stables.confirmWithdraw")
    : acceptArmed
      ? intl.get("stables.acceptQuoteConfirm")
      : intl.get("stables.acceptQuote");

  const handleDialogClose = (_, reason) => {
    if (step === "security" && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    if (isWithdraw && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    if (passwordLoading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={isWithdraw}
      fullWidth
      maxWidth="xs"
      PaperProps={{ className: "!rounded-2xl !shadow-card overflow-hidden" }}
    >
      {step === "security" ? (
        <PasswordConfirm
          variant="embedded"
          open
          submitPassword={submitPassword}
          onBack={handleSecurityBack}
          onClose={onClose}
          error={passwordError}
          loading={passwordLoading}
          onDismissError={onDismissPasswordError}
          stepUpOptions={stepUpOptions}
        />
      ) : (
        <div className="relative px-5 py-6 sm:px-6">
          <ModalCloseButton
            onClick={onClose}
            className="absolute right-3 top-3"
            disabled={passwordLoading}
          />
          <h2 className="pr-8 text-center text-lg font-bold text-brand-dark">
            {QUOTE_TITLES[quote.operation]}
          </h2>

          <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 px-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
            {isWithdraw ? (
              <>
                <QuoteRow
                  label="Rede"
                  value={quote.chain}
                />
                <QuoteRow
                  label="Destino"
                  value={quote.destinationDisplay || quote.destination}
                />
                <QuoteRow
                  label="Você saca"
                  value={formatQuoteCrypto(quote.stableAmount)}
                  suffix={quote.assetLabel}
                />
                <QuoteRow
                  label="Você recebe"
                  value={formatQuoteCrypto(quote.netStableAmount)}
                  suffix={quote.assetLabel}
                />
                <QuoteRow
                  label="Taxa"
                  value={formatQuoteCrypto(quote.feeStable)}
                  suffix={quote.assetLabel}
                  muted
                />
                {Number(quote.networkFeeStable) > 0 && (
                  <QuoteRow
                    label="Taxa da rede"
                    value={formatQuoteCrypto(quote.networkFeeStable)}
                    suffix={quote.assetLabel}
                    muted
                  />
                )}
                <QuoteRow
                  label="Percentual da taxa"
                  value={formatQuotePercent(quote.feePercent)}
                  suffix="%"
                  muted
                />
              </>
            ) : isSwap ? (
              <>
                <QuoteRow
                  label="Preço"
                  value={formatQuoteCrypto(
                    quote.fromAmount > 0 ? quote.netToAmount / quote.fromAmount : quote.price
                  )}
                  suffix={quote.toAssetLabel}
                />
                <QuoteRow
                  label="Enviado"
                  value={formatQuoteCrypto(quote.fromAmount)}
                  suffix={quote.fromAssetLabel}
                />
                <QuoteRow
                  label="Recebido"
                  value={formatQuoteCrypto(quote.netToAmount)}
                  suffix={quote.toAssetLabel}
                />
                <QuoteRow
                  label="Taxa"
                  value={formatQuoteCrypto(quote.feeStable)}
                  suffix={quote.toAssetLabel}
                  muted
                />
                <QuoteRow
                  label="Percentual da taxa"
                  value={formatQuotePercent(quote.feePercent)}
                  suffix="%"
                  muted
                />
              </>
            ) : (
              <>
                <QuoteRow
                  label="Preço"
                  value={formatQuotePrice(quote.price)}
                  suffix="BRL"
                />
                {isBuy ? (
                  <>
                    <QuoteRow
                      label="Você compra"
                      value={formatQuoteCrypto(buyDisplay.stableAmount)}
                      suffix={quote.assetLabel}
                    />
                    <QuoteRow
                      label="Você paga"
                      value={formatQuoteBrl(buyDisplay.brlAmount)}
                      suffix="BRL"
                    />
                  </>
                ) : (
                  <>
                    <QuoteRow
                      label="Você vende"
                      value={formatQuoteCrypto(quote.stableAmount)}
                      suffix={quote.assetLabel}
                    />
                    <QuoteRow
                      label="Você recebe"
                      value={formatQuoteBrl(quote.netBrlAmount)}
                      suffix="BRL"
                    />
                  </>
                )}
                <QuoteRow
                  label="Taxa"
                  value={isBuy ? formatQuoteCrypto(quote.feeStable) : formatQuoteBrl(quote.feeBrl)}
                  suffix={isBuy ? quote.assetLabel : "BRL"}
                  muted
                />
                <QuoteRow
                  label="Percentual da taxa"
                  value={formatQuotePercent(quote.feePercent)}
                  suffix="%"
                  muted
                />
              </>
            )}
          </div>

          {!isWithdraw && (
            <div className="mt-5 flex flex-col items-center gap-2">
              <span className="rounded-full bg-gray-100 px-4 py-1 text-xs font-medium text-gray-600">
                Tempo restante: {formatQuoteTimer(secondsLeft)}
              </span>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-full rounded-full ${barTransition ? "transition-all duration-1000 ease-linear" : ""}`}
                  style={{
                    width: `${barWidth}%`,
                    background: "linear-gradient(135deg, #fcd177, #e7a73f)",
                  }}
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              className={`stables-quote-btn-accept${acceptArmed ? " stables-quote-btn-accept--armed" : ""}`}
              onClick={handleAcceptClick}
              disabled={acceptDisabled}
              aria-live="polite"
            >
              {acceptLabel}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export const buildStablesQuote = ({ operation, asset, transaction }) => ({
  id: `${operation}-${Date.now()}`,
  operation,
  assetId: asset.id,
  assetLabel: asset.label,
  price: asset.priceString ?? asset.rate,
  ...transaction,
});

export const buildSwapQuote = ({ fromAsset, toAsset, transaction }) => ({
  id: `swap-${Date.now()}`,
  operation: "swap",
  fromAssetId: fromAsset.id,
  toAssetId: toAsset.id,
  fromAssetLabel: fromAsset.label,
  toAssetLabel: toAsset.label,
  ...transaction,
});

const shortenDestination = (value = "") => {
  const text = String(value).trim();
  if (text.length <= 20) return text;
  return `${text.slice(0, 10)}…${text.slice(-8)}`;
};

export const buildWithdrawQuote = ({ asset, transaction }) => ({
  id: `withdraw-${Date.now()}`,
  operation: "withdraw",
  assetId: asset.id,
  assetLabel: asset.label,
  price: asset.priceString ?? asset.rate,
  destinationDisplay: shortenDestination(transaction.destination),
  ...transaction,
});
